import axios from '../api';
import { API_URL } from '../api/api1';

// Email service using main API backend
const EMAIL_SERVICE_URL = API_URL;

const emailService = {
  // Send email to student via Node.js service
  sendStudentEmail: async (studentId, emailType, customMessage = '', additionalData = {}) => {
    try {
      const { studentEmail, studentName, libraryName, amount, dueDate, expiryDate, paymentDate, subscriptionType } = additionalData;
      
      let endpoint = '/api/email/send';
      let payload = {
        to: studentEmail,
        subject: customMessage || 'Notification',
        text: customMessage,
        emailType: emailType,
        studentData: additionalData
      };

      // Use specific endpoints for different email types
      switch (emailType) {
        case 'welcome':
          endpoint = '/api/email/welcome';
          payload = { studentEmail, studentName, libraryName };
          break;
        case 'payment_reminder':
          endpoint = '/api/email/payment-reminder';
          payload = { studentEmail, studentName, amount, dueDate, libraryName };
          break;
        case 'expiry_reminder':
          endpoint = '/api/email/expiry-reminder';
          payload = { studentEmail, studentName, expiryDate, libraryName };
          break;
        case 'otp':
          endpoint = '/api/email/otp';
          payload = { studentEmail, studentName, otpCode: additionalData.otpCode, purpose: additionalData.purpose };
          break;
        case 'receipt':
          endpoint = '/api/test-email';
          payload = {
            to_email: studentEmail,
            subject: `Payment Receipt - ${libraryName || 'Library'}`,
            message: `Payment receipt for ${studentName}`,
            is_html: false,
            receipt_data: {
              studentName: studentName,
              studentEmail: studentEmail,
              amount: amount,
              paymentDate: paymentDate,
              subscriptionType: subscriptionType,
              libraryName: libraryName,
              paymentMethod: additionalData.paymentMethod,
              transactionId: additionalData.transactionId,
              receiptNumber: additionalData.receiptNumber,
              studentPhone: additionalData.studentPhone,
              studentAddress: additionalData.studentAddress,
              planName: additionalData.planName,
              seatNumber: additionalData.seatNumber
            },
            generate_pdf: true
          };
          break;
        default:
          // For custom or unknown email types, use the working test-email endpoint
          endpoint = '/api/test-email';
          payload = {
            to_email: studentEmail,
            subject: customMessage || 'Notification',
            message: customMessage,
            is_html: false,
            emailType: emailType,
            studentData: additionalData
          };
          break;
      }

      const response = await fetch(`${EMAIL_SERVICE_URL}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
        signal: AbortSignal.timeout(10000) // 10 second timeout
      });
      
      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Send student email error:', error);
      throw error.response?.data || { error: 'Failed to send email' };
    }
  },

  // Send email to tenant
  sendTenantEmail: async (tenantId, emailType, customMessage = '') => {
    try {
      const response = await axios.post('/api/send-tenant-email', {
        tenant_id: tenantId,
        email_type: emailType,
        custom_message: customMessage
      });
      return response.data;
    } catch (error) {
      console.error('Send tenant email error:', error);
      throw error.response?.data || { error: 'Failed to send email' };
    }
  },

  // Send bulk email to students
  sendBulkEmail: async (studentIds, subject, message, isHtml = false) => {
    try {
      const response = await axios.post('/api/send-bulk-email', {
        student_ids: studentIds,
        subject: subject,
        message: message,
        is_html: isHtml
      });
      return response.data;
    } catch (error) {
      console.error('Send bulk email error:', error);
      throw error.response?.data || { error: 'Failed to send bulk email' };
    }
  },

  // Send email to super admin
  sendSuperAdminEmail: async (adminId, message, notificationType = 'general') => {
    try {
      const response = await axios.post('/api/send-superadmin-email', {
        admin_id: adminId,
        message: message,
        notification_type: notificationType
      });
      return response.data;
    } catch (error) {
      console.error('Send super admin email error:', error);
      throw error.response?.data || { error: 'Failed to send email' };
    }
  },

  // Send OTP via email via Node.js service
  sendOTPEmail: async (email, otpCode, purpose = 'verification', name = 'User') => {
    try {
      const response = await fetch(`${EMAIL_SERVICE_URL}/api/email/otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          studentEmail: email,
          studentName: name,
          otpCode: otpCode,
          purpose: purpose
        }),
        signal: AbortSignal.timeout(10000) // 10 second timeout
      });
      
      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Send OTP email error:', error);
      throw error.response?.data || { error: 'Failed to send OTP email' };
    }
  },

  // Test email functionality via Node.js service
  testEmail: async (toEmail, subject = 'Test Email', message = 'This is a test email from the library management system') => {
    try {
      const response = await fetch(`${EMAIL_SERVICE_URL}/api/email/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          to: toEmail,
          subject: subject,
          text: message,
          emailType: 'test'
        }),
        signal: AbortSignal.timeout(10000) // 10 second timeout
      });
      
      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Test email error:', error);
      throw error.response?.data || { error: 'Failed to send test email' };
    }
  },

  // Send welcome email to student
  sendWelcomeEmail: async (studentId, libraryName = null) => {
    return emailService.sendStudentEmail(studentId, 'welcome', '', { library_name: libraryName });
  },

  // Send payment reminder email
  sendPaymentReminderEmail: async (studentId, amount, dueDate, libraryName = null, additionalData = {}) => {
    // Create AbortController for timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout

    try {
      const response = await fetch(`${EMAIL_SERVICE_URL}/api/email/payment-reminder`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          studentEmail: additionalData.studentEmail,
          studentName: additionalData.studentName,
          amount: amount,
          dueDate: dueDate,
          libraryName: libraryName
        }),
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      const result = await response.json();
      return result;
    } catch (error) {
      clearTimeout(timeoutId);
      console.error('Send payment reminder email error:', error);
      
      // Handle timeout specifically
      if (error.name === 'AbortError') {
        const timeoutError = new Error('Request timed out. Please try again.');
        timeoutError.name = 'TimeoutError';
        throw timeoutError;
      }
      
      throw error.response?.data || new Error('Failed to send payment reminder email');
    }
  },

  // Send subscription expiry reminder email
  sendExpiryReminderEmail: async (studentId, expiryDate, libraryName = null) => {
    return emailService.sendStudentEmail(studentId, 'expiry_reminder', '', {
      expiry_date: expiryDate,
      library_name: libraryName
    });
  },

  // Send receipt email
  sendReceiptEmail: async (studentId, amount, paymentDate, subscriptionType, libraryName = null, additionalData = {}) => {
    try {
      // Use the configured axios client so tenant/auth headers are attached.
      const response = await axios.post('/api/test-email', {
        to_email: additionalData.studentEmail,
        subject: `Payment Receipt - ${libraryName || 'Library'}`,
        message: additionalData.htmlContent || `Payment receipt for ${additionalData.studentName}`,
        is_html: !!additionalData.htmlContent,
        receipt_data: {
          studentName: additionalData.studentName,
          studentEmail: additionalData.studentEmail,
          studentPhone: additionalData.studentPhone,
          studentAddress: additionalData.studentAddress,
          libraryName,
          amount,
          paymentDate,
          subscriptionType,
          planName: additionalData.planName,
          seatNumber: additionalData.seatNumber,
          paymentMethod: additionalData.paymentMethod,
          transactionId: additionalData.transactionId,
          receiptNumber: additionalData.receiptNumber,
          previousPaid: additionalData.previousPaid,
          totalPaid: additionalData.totalPaid,
          planAmount: additionalData.planAmount,
          remainingDue: additionalData.remainingDue,
          paymentHistory: additionalData.paymentHistory,
          status: additionalData.status
        },
        generate_pdf: true
      }, { timeout: 15000 });
      return response.data;
    } catch (error) {
      console.error('Send receipt email error:', error);
      throw error.response?.data || { error: 'Failed to send receipt email' };
    }
  },

  // Send custom email to student
  sendCustomEmail: async (studentId, subject, message) => {
    return emailService.sendStudentEmail(studentId, 'custom', message);
  },

  // Send welcome email to tenant
  sendTenantWelcomeEmail: async (tenantId, tenantName, adminName) => {
    return emailService.sendTenantEmail(tenantId, 'welcome', '', { tenant_name: tenantName, admin_name: adminName });
  },

  // Send custom email to tenant
  sendCustomTenantEmail: async (tenantId, subject, message) => {
    return emailService.sendTenantEmail(tenantId, 'custom', message);
  }
};

export default emailService;
